import React from 'react';
import { PortableText, PortableTextReactComponents } from '@portabletext/react';
import { Type } from '@spotify-internal/encore-web';
import styled from 'styled-components';
import {
  PortableTextMarkComponentProps,
  PortableTextProps,
  PortableTextTypeComponentProps,
} from '@portabletext/react/src/types';

import {
  Accordion,
  ImageRef,
  InlineImageRef,
  InlineSnippet,
  InternalLinkMarkDef,
  LinkMarkDef,
  MarketSpecificContent,
  SnippetEmbed,
  Table,
  VideoEmbed,
} from 'lib/sanity/types';
import ArticleAccordion from 'components/ArticleAccordion';
import ArticleImage from 'components/ArticleImage';
import ArticleMarketSpecificContent from 'components/ArticleMarketSpecificContent';
import ArticleSnippet from 'components/ArticleSnippet';
import ArticleTable from 'components/ArticleTable';
import ArticleVideoEmbed from 'components/ArticleVideoEmbed';
import InlineLink from 'components/InlineLink';
import ArticleInternalLink from 'components/ArticleInternalLink/ArticleInternalLink';

const HeaderType = styled(Type)`
  padding-top: 0.5em;
  padding-bottom: 0;
  word-break: break-word;
  // If the current language is supported, hyphens:auto will know where to insert a "-" in a fitting place
  hyphens: auto;
`;

const components: Partial<PortableTextReactComponents> = {
  unknownType: () => null,
  marks: {
    internalLink: ({ value, children }: PortableTextMarkComponentProps<InternalLinkMarkDef>) => (
      <ArticleInternalLink mark={value!} children={children} />
    ),
    link: ({ value, children }: PortableTextMarkComponentProps<LinkMarkDef>) => (
      <InlineLink href={value!.href}>{children}</InlineLink>
    ),
  },
  block: {
    h2: ({ children }) => (
      <HeaderType forwardedAs="h2" variant="canon">
        {children}
      </HeaderType>
    ),
    h3: ({ children }) => (
      <HeaderType forwardedAs="h3" variant="cello">
        {children}
      </HeaderType>
    ),
  },
  types: {
    accordion: ({ value }: PortableTextTypeComponentProps<Accordion>) => <ArticleAccordion accordion={value} />,
    imageReference: ({ value }: PortableTextTypeComponentProps<ImageRef>) => <ArticleImage image={value.image} />,
    marketSpecificContent: ({ value }: PortableTextTypeComponentProps<MarketSpecificContent>) => (
      <ArticleMarketSpecificContent content={value.content} />
    ),
    snippetEmbed: ({ value }: PortableTextTypeComponentProps<SnippetEmbed>) => (
      <ArticleSnippet snippet={value.snippet} />
    ),
    table: ({ value }: PortableTextTypeComponentProps<Table>) => <ArticleTable table={value} />,
    videoEmbed: ({ value }: PortableTextTypeComponentProps<VideoEmbed>) => <ArticleVideoEmbed videoEmbed={value} />,
    inlineSnippet: ({ value }: PortableTextTypeComponentProps<InlineSnippet>) => (
      <PortableText
        components={{
          block: ({ children }) => <>{children}</>,
        }}
        value={value.snippet.content}
      />
    ),
    inlineImageRef: ({ value }: PortableTextTypeComponentProps<InlineImageRef>) => (
      <ArticleImage image={value.inlineImage} inline />
    ),
  },
};

const BlockSerializer = (props: PortableTextProps) => <PortableText components={components} {...props} />;

export default BlockSerializer;
